/**
 * Sample closure costs.
 *
 * Chosen to make the module's point rather than to look tidy: the stand with
 * the MOST traffic is the cheapest to close, and the one with the least is the
 * one that strands an aircraft. That inversion is the whole reason the module
 * exists, and a mock where cost tracked movement count would quietly teach the
 * opposite lesson to anyone demoing it.
 *
 *   A12  busy, and entirely absorbed — nine neighbours take the same code
 *   C2   quiet, but one of only two stands taking a code-E widebody
 *   R7   absorbed, at the price of bussing passengers from a remote stand
 *   any other code  the schedule does not cover the window
 */
import type { ClosureCost, CriticalReport } from "../domain/capacity";
import type { ClosureQuery } from "../api/dataSource";

const ASSUMPTION =
  "This is a counterfactual: it is where these flights WOULD have gone had the position been closed, " +
  "tested against the positions that remain. It is not a record of anything that happened.";

export function mockClosureCost(q: ClosureQuery): ClosureCost {
  const p = q.positionCode.trim().toUpperCase();
  const window = { from: q.from, to: q.to };

  if (p.startsWith("A")) {
    return {
      position: p,
      window,
      known: true,
      movements: 14,
      absorbed: 14,
      bussed: 0,
      stranded: 0,
      undetermined: 0,
      hours: 8,
      scheduleCoverage: "the schedule covers the whole window",
      assumption: ASSUMPTION,
      detail: [
        { flight: "SV1043", sizeCode: "C", movesTo: "A11", why: "same size code, free at that hour" },
        { flight: "SV1128", sizeCode: "C", movesTo: "A14", why: "same size code, free at that hour" },
        { flight: "XY204", sizeCode: "C", movesTo: "A09", why: "same size code, free at that hour" },
      ],
    };
  }

  if (p.startsWith("C")) {
    return {
      position: p,
      window,
      known: true,
      movements: 3,
      absorbed: 1,
      bussed: 0,
      stranded: 2,
      undetermined: 0,
      hours: 8,
      scheduleCoverage: "the schedule covers the whole window",
      assumption: ASSUMPTION,
      detail: [
        {
          flight: "SV778",
          sizeCode: "E",
          movesTo: undefined,
          why: "only C1 also takes a code-E, and it is occupied for the whole window",
        },
        {
          flight: "SV781",
          sizeCode: "E",
          movesTo: undefined,
          why: "only C1 also takes a code-E, and it is occupied for the whole window",
        },
        { flight: "SV642", sizeCode: "C", movesTo: "C4", why: "same size code, free at that hour" },
      ],
    };
  }

  if (p.startsWith("R")) {
    return {
      position: p,
      window,
      known: true,
      movements: 6,
      absorbed: 4,
      bussed: 2,
      stranded: 0,
      undetermined: 0,
      hours: 8,
      scheduleCoverage: "the schedule covers the whole window",
      assumption: ASSUMPTION,
      detail: [
        { flight: "SV310", sizeCode: "C", movesTo: "R9", why: "same size code, free at that hour" },
        {
          flight: "SV455",
          sizeCode: "C",
          movesTo: "V2 (remote)",
          why: "no contact stand free; a remote stand takes it and the passengers are bussed",
        },
      ],
    };
  }

  /* The honest default: unknown, not free. */
  return {
    position: p,
    window,
    known: false,
    whyNot:
      "the schedule does not cover the whole of this window. Any cost this returned would be low only " +
      "because nobody has loaded the flights for it.",
    movements: 0,
    absorbed: 0,
    bussed: 0,
    stranded: 0,
    undetermined: 0,
    detail: [],
  };
}

export function mockCriticalPositions(from: string, to: string): CriticalReport {
  return {
    window: { from, to },
    known: true,
    note:
      "A position is critical when closing it strands traffic — not when it is busy. The flexible list is " +
      "longer than operators expect, and that is where maintenance should go first.",
    scheduleCoverage: "the schedule covers the whole window",
    critical: [
      { position: "C2", stranded: 2, bussed: 0, movements: 3, undetermined: 0 },
      { position: "C1", stranded: 1, bussed: 0, movements: 4, undetermined: 0 },
    ],
    flexible: [
      { position: "A12", stranded: 0, bussed: 0, movements: 14, undetermined: 0 },
      { position: "A11", stranded: 0, bussed: 0, movements: 12, undetermined: 0 },
      { position: "R7", stranded: 0, bussed: 2, movements: 6, undetermined: 0 },
      { position: "A09", stranded: 0, bussed: 0, movements: 9, undetermined: 0 },
    ],
  };
}
